/**
 * Legal Documents Page — /legal-docs
 *
 * Two-pane workspace:
 *   Left  — list of documents grouped by company (Iceberg CMS, Image
 *           Traders, …), each row showing title · doc-type · version.
 *   Right — selected document editor:
 *             • Company picker (free-text with suggestions from existing docs)
 *             • Client link picker (optional link to a client)
 *             • Title + doc-type + effective date + footer note
 *             • Markdown body editor (mono textarea) + live preview
 *             • Version display + auto-bump on save
 *             • "Save (bump minor)" and "Publish major version" buttons
 *             • "Download PDF" button
 *             • Version history panel showing every previous save with
 *               a "View" button to load that snapshot into the editor.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText, Plus, Download, Save, Rocket, History, Trash2,
  Loader2, Building2, Sparkles, X, ChevronRight, Copy,
} from "lucide-react";
import { toast } from "sonner";
import MarkdownEditor from "@/components/MarkdownEditor";
import LegalAgreementsCard from "@/components/LegalAgreementsCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const DOC_TYPES = [
  { value: "sla", label: "Service Level Agreement" },
  { value: "terms", label: "Terms & Conditions" },
  { value: "use_policy", label: "Acceptable Use Policy" },
  { value: "time_materials", label: "Time & Materials Rates" },
  { value: "privacy", label: "Privacy Policy" },
  { value: "custom", label: "Custom / Other" },
];

function errText(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length > 0) return d[0]?.msg || fallback;
  return err?.message || fallback;
}

function docTypeLabel(t) {
  return DOC_TYPES.find((x) => x.value === t)?.label || "Legal Document";
}

/**
 * Ultra-minimal markdown preview — matches the backend PDF renderer's
 * subset (H1/H2/H3, bold, italic, bullets, tables, HR). Not a full
 * markdown parser but enough to give the admin confidence about the
 * layout before they save.
 */
function LivePreview({ md }) {
  const html = useMemo(() => {
    const lines = (md || "").replace(/\r/g, "").split("\n");
    const out = [];
    let i = 0;

    const inline = (s) =>
      s.replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")
        .replace(/\*([^*]+)\*/g, "<i>$1</i>")
        .replace(/`([^`]+)`/g, '<code class="bg-zinc-100 rounded px-1 text-[12px]">$1</code>');

    while (i < lines.length) {
      const ln = lines[i].trimEnd();
      if (!ln.trim()) { i++; continue; }
      if (["---", "***", "___"].includes(ln.trim())) {
        out.push('<hr class="my-3 border-t border-[#002FA7]/40" />'); i++; continue;
      }
      if (ln.startsWith("### ")) { out.push(`<h3 class="font-display font-bold text-[13px] text-zinc-800 mt-3 mb-1">${inline(ln.slice(4))}</h3>`); i++; continue; }
      if (ln.startsWith("## "))  { out.push(`<h2 class="font-display font-bold text-[15px] text-[#002FA7] mt-4 mb-1.5">${inline(ln.slice(3))}</h2>`); i++; continue; }
      if (ln.startsWith("# "))   { out.push(`<h1 class="font-display font-extrabold text-[20px] text-zinc-900 mt-5 mb-2">${inline(ln.slice(2))}</h1>`); i++; continue; }
      // Table
      if (ln.startsWith("|") && ln.endsWith("|")) {
        const tbl = [];
        while (i < lines.length && lines[i].trim().startsWith("|") && lines[i].trim().endsWith("|")) {
          tbl.push(lines[i].trim()); i++;
        }
        const rows = tbl
          .map((r) => r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim()))
          .filter((r) => !(r.every((c) => /^[- :]+$/.test(c)) && r.some((c) => c.includes("-"))));
        if (rows.length) {
          const head = rows[0]; const body = rows.slice(1);
          out.push('<table class="min-w-full text-[12px] border border-zinc-200 rounded-sm my-2"><thead class="bg-zinc-100"><tr>' +
            head.map((c) => `<th class="p-1.5 text-left border-r border-zinc-200 last:border-r-0 font-semibold">${inline(c)}</th>`).join("") + '</tr></thead><tbody>' +
            body.map((r) => `<tr class="border-t border-zinc-100">${r.map((c) => `<td class="p-1.5 border-r border-zinc-100 last:border-r-0 align-top">${inline(c)}</td>`).join("")}</tr>`).join("") + '</tbody></table>');
        }
        continue;
      }
      // Bullets
      if (/^[-*•]\s/.test(ln)) {
        const items = [];
        while (i < lines.length && /^[-*•]\s/.test(lines[i].trim())) {
          items.push(lines[i].trim().replace(/^[-*•]\s/, "")); i++;
        }
        out.push('<ul class="list-none pl-2 my-1.5 space-y-1">' +
          items.map((t) => `<li class="flex gap-2 text-[12px] leading-5"><span class="text-[#002FA7]">•</span><span>${inline(t)}</span></li>`).join("") + '</ul>');
        continue;
      }
      // Ordered
      if (/^\s*\d+\.\s/.test(ln)) {
        const items = [];
        while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
          items.push(lines[i].trim().replace(/^\d+\.\s/, "")); i++;
        }
        out.push('<ol class="list-decimal ml-5 my-1.5 space-y-1 text-[12px] leading-5">' +
          items.map((t) => `<li>${inline(t)}</li>`).join("") + '</ol>');
        continue;
      }
      // Paragraph
      const para = [ln]; i++;
      while (i < lines.length && lines[i].trim() && !/^(#|\||\s*\d+\.\s|[-*•]\s|---|\*\*\*|___)/.test(lines[i])) {
        para.push(lines[i].trim()); i++;
      }
      out.push(`<p class="text-[12px] leading-5 text-zinc-700 my-1.5">${inline(para.join(" "))}</p>`);
    }
    return out.join("\n");
  }, [md]);
  return (
    <div className="prose-none rounded-sm bg-white border border-zinc-200 p-4 max-h-[70vh] overflow-y-auto"
      dangerouslySetInnerHTML={{ __html: html }}
      data-testid="legal-doc-preview" />
  );
}

export default function LegalDocsPage() {
  const [docs, setDocs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [versions, setVersions] = useState([]);
  const [clients, setClients] = useState([]);
  // Sender agencies — the single source of truth for the "Company"
  // selector. Editable via Settings → Sender agencies.
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [newDialog, setNewDialog] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Duplicate-to-another-agency dialog
  const [dupOpen, setDupOpen] = useState(false);
  const [dupCompany, setDupCompany] = useState("");
  const [dupTitle, setDupTitle] = useState("");
  const [dupBusy, setDupBusy] = useState(false);

  // New-doc form
  const [nf, setNf] = useState({
    company_name: "Iceberg CMS", client_id: "",
    title: "", doc_type: "custom", version_style: "dot",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ldocs }, cli, ag] = await Promise.all([
        api.get("/legal-documents"),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/agency-profiles").catch(() => ({ data: { agency_profiles: [] } })),
      ]);
      setDocs(ldocs || []);
      setClients(cli.data || []);
      setAgencies(ag.data?.agency_profiles || []);
      if (!selectedId && (ldocs || []).length > 0) {
        setSelectedId(ldocs[0].document_id);
      }
    } catch (e) {
      toast.error(errText(e, "Failed to load documents"));
    } finally { setLoading(false); }
  }, [selectedId]);
  useEffect(() => { load(); }, [load]);

  const loadOne = useCallback(async (id) => {
    if (!id) { setSelected(null); return; }
    try {
      const { data } = await api.get(`/legal-documents/${id}`);
      setSelected(data);
      setDirty(false);
      const v = await api.get(`/legal-documents/${id}/versions`).catch(() => ({ data: [] }));
      setVersions(v.data || []);
    } catch (e) { toast.error(errText(e, "Failed to load document")); }
  }, []);
  useEffect(() => { loadOne(selectedId); }, [selectedId, loadOne]);

  const grouped = useMemo(() => {
    const g = {};
    for (const d of docs) {
      const c = d.company_name || "Unassigned";
      if (!g[c]) g[c] = [];
      g[c].push(d);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [docs]);

  // Company options come EXCLUSIVELY from the Sender Agencies profiles.
  // Includes any historical company_name values from existing legal docs
  // that don't match a current agency, so we don't silently orphan old
  // documents when an agency is renamed/deleted.
  const companyOptions = useMemo(() => {
    const set = new Set();
    for (const a of agencies) if (a?.name) set.add(a.name);
    for (const d of docs) if (d?.company_name) set.add(d.company_name);
    return Array.from(set).sort();
  }, [agencies, docs]);

  const patchField = (field, value) => {
    setSelected((s) => ({ ...s, [field]: value }));
    setDirty(true);
  };

  const save = async ({ skip_bump = false, change_note = "" } = {}) => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = {
        company_name: selected.company_name,
        client_id: selected.client_id || null,
        title: selected.title,
        doc_type: selected.doc_type,
        body_md: selected.body_md,
        effective_date: selected.effective_date || null,
        footer_note: selected.footer_note || null,
        skip_version_bump: skip_bump,
        change_note,
      };
      const { data } = await api.patch(`/legal-documents/${selected.document_id}`, payload);
      setSelected(data);
      setDirty(false);
      toast.success(skip_bump ? "Saved (no version bump)" : `Saved as ${data.version_string}`);
      const v = await api.get(`/legal-documents/${selected.document_id}/versions`);
      setVersions(v.data || []);
      // Refresh the list to update version pills / updated_at
      const list = await api.get("/legal-documents");
      setDocs(list.data || []);
    } catch (e) {
      toast.error(errText(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const publishMajor = async () => {
    if (!selected) return;
    if (!window.confirm(`Publish a new major version? Current is ${selected.version_string}.`)) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/legal-documents/${selected.document_id}/publish`);
      setSelected(data);
      toast.success(`Published ${data.version_string}`);
      const v = await api.get(`/legal-documents/${selected.document_id}/versions`);
      setVersions(v.data || []);
      const list = await api.get("/legal-documents");
      setDocs(list.data || []);
    } catch (e) { toast.error(errText(e, "Publish failed")); }
    finally { setPublishing(false); }
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/legal-documents/${selected.document_id}`);
      toast.success("Deleted");
      setSelected(null); setSelectedId(null);
      load();
    } catch (e) { toast.error(errText(e, "Delete failed")); }
  };

  const downloadPdf = async () => {
    if (!selected) return;
    try {
      const token = localStorage.getItem("token") || "";
      const url = `${BACKEND}/api/legal-documents/${selected.document_id}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${(selected.company_name || "Iceberg").replace(/\s+/g, "_")}_${selected.title.replace(/\s+/g, "_")}_${selected.version_string}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error("PDF download failed"); }
  };

  const seedIceberg = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/legal-documents/seed");
      toast.success(`Seeded ${data.count} Iceberg document${data.count === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(errText(e, "Seed failed")); }
    finally { setSeeding(false); }
  };

  const createDoc = async () => {
    if (!nf.title || !nf.company_name) {
      toast.error("Company + title are required");
      return;
    }
    try {
      const { data } = await api.post("/legal-documents", {
        ...nf, client_id: nf.client_id || null,
        body_md: `# ${nf.title}\n\n_Version 1.0_\n\nStart writing here…`,
      });
      toast.success("Document created");
      setNewDialog(false);
      setNf({ company_name: "Iceberg CMS", client_id: "", title: "",
              doc_type: "custom", version_style: "dot" });
      setSelectedId(data.document_id);
      load();
    } catch (e) { toast.error(errText(e, "Create failed")); }
  };

  const loadVersion = async (v) => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    setSelected((s) => ({ ...s, body_md: v.body_md, title: v.title }));
    setDirty(true);
    toast.info(`Loaded ${v.version_string} into editor (not saved yet)`);
    setHistoryOpen(false);
  };

  const openDuplicate = () => {
    if (!selected) return;
    setDupCompany(companyOptions.find((c) => c !== selected.company_name) || "");
    setDupTitle(selected.title || "");
    setDupOpen(true);
  };

  const duplicate = async () => {
    if (!dupCompany) { toast.error("Pick the target agency"); return; }
    setDupBusy(true);
    try {
      const { data } = await api.post(`/legal-documents/${selected.document_id}/duplicate`, {
        company_name: dupCompany, title: dupTitle || null,
      });
      toast.success(`Copied to ${dupCompany} as ${data.version_string} draft`);
      setDupOpen(false);
      setSelectedId(data.document_id);
      load();
    } catch (e) { toast.error(errText(e, "Duplicate failed")); }
    finally { setDupBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="legal-docs-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display font-bold text-2xl">Legal Documents</h1>
          <p className="text-xs text-zinc-500">
            SLA, T&amp;C, Use Policy and other legal documents, per company.
            Each save auto-bumps a minor version; Publish bumps the major.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {docs.length === 0 && (
            <Button variant="outline" size="sm" onClick={seedIceberg} disabled={seeding}
              data-testid="legal-docs-seed-btn">
              <Sparkles className="h-4 w-4 mr-1.5" />
              {seeding ? "Seeding…" : "Load Iceberg templates"}
            </Button>
          )}
          <Button size="sm" onClick={() => setNewDialog(true)} data-testid="legal-docs-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New document
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* LEFT — list */}
          <Card className="col-span-12 lg:col-span-3 rounded-sm">
            <CardContent className="p-3 space-y-4">
              {grouped.length === 0 && (
                <div className="text-xs text-zinc-500 rounded-sm bg-zinc-50 p-3">
                  No documents yet — click <b>New document</b> or <b>Load Iceberg templates</b>.
                </div>
              )}
              {grouped.map(([company, list]) => (
                <div key={company}>
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
                    <Building2 className="h-3.5 w-3.5" /> {company}
                  </div>
                  <div className="space-y-0.5">
                    {list.map((d) => {
                      const active = d.document_id === selectedId;
                      return (
                        <button key={d.document_id}
                          onClick={() => setSelectedId(d.document_id)}
                          data-testid={`legal-doc-item-${d.document_id}`}
                          className={`w-full text-left rounded-sm px-2 py-1.5 text-sm flex items-center gap-2 ${
                            active ? "bg-[#002FA7]/10 text-[#002FA7]" : "hover:bg-zinc-100 text-zinc-700"
                          }`}>
                          <FileText className="h-4 w-4 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium leading-tight">{d.title}</div>
                            <div className="text-[10px] text-zinc-500 truncate">
                              {docTypeLabel(d.doc_type)} · {d.version_string}
                            </div>
                          </div>
                          {active && <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* RIGHT — editor */}
          <div className="col-span-12 lg:col-span-9 space-y-4">
            {!selected ? (
              <Card className="rounded-sm"><CardContent className="p-8 text-center text-sm text-zinc-500">
                Select a document on the left, or click <b>New document</b>.
              </CardContent></Card>
            ) : (
              <>
                {/* Toolbar */}
                <Card className="rounded-sm">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="rounded-sm bg-[#002FA7]/10 text-[#002FA7] border-transparent"
                          data-testid="legal-doc-version-badge">
                          {selected.version_string}
                        </Badge>
                        <Badge variant="outline" className="rounded-sm text-[10px]">
                          {docTypeLabel(selected.doc_type)}
                        </Badge>
                        <Badge variant="outline"
                          className={`rounded-sm text-[10px] ${selected.status === "published"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                          {selected.status || "draft"}
                        </Badge>
                        {dirty && (
                          <Badge className="rounded-sm bg-amber-100 text-amber-800 border-transparent text-[10px]"
                            data-testid="legal-doc-dirty-badge">unsaved changes</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="outline"
                          onClick={() => setPreview((v) => !v)}
                          data-testid="legal-doc-preview-toggle">
                          {preview ? "Hide preview" : "Show preview"}
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => setHistoryOpen(true)}
                          data-testid="legal-doc-history-btn">
                          <History className="h-4 w-4 mr-1.5" /> History ({versions.length})
                        </Button>
                        <Button size="sm" variant="outline" onClick={downloadPdf}
                          data-testid="legal-doc-pdf-btn">
                          <Download className="h-4 w-4 mr-1.5" /> PDF
                        </Button>
                        <Button size="sm" variant="outline" onClick={openDuplicate}
                          data-testid="legal-doc-duplicate-btn">
                          <Copy className="h-4 w-4 mr-1.5" /> Duplicate
                        </Button>
                        <Button size="sm" variant="outline" onClick={publishMajor}
                          disabled={publishing} data-testid="legal-doc-publish-btn">
                          <Rocket className="h-4 w-4 mr-1.5" />
                          {publishing ? "Publishing…" : "Publish major"}
                        </Button>
                        <Button size="sm" onClick={() => save()}
                          disabled={saving || !dirty} data-testid="legal-doc-save-btn">
                          <Save className="h-4 w-4 mr-1.5" />
                          {saving ? "Saving…" : "Save (bump minor)"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={remove}
                          className="text-red-600 hover:text-red-700"
                          data-testid="legal-doc-delete-btn">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Metadata form */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                      <div className="md:col-span-2">
                        <Label className="text-[11px]">Company (from Sender agencies)</Label>
                        <Select value={selected.company_name || ""}
                          onValueChange={(v) => patchField("company_name", v)}>
                          <SelectTrigger data-testid="legal-doc-company-select">
                            <SelectValue placeholder="Pick a sender agency" />
                          </SelectTrigger>
                          <SelectContent>
                            {companyOptions.length === 0 && (
                              <div className="px-3 py-2 text-[11px] text-zinc-500">
                                No sender agencies yet — add one under
                                Settings → Sender agencies.
                              </div>
                            )}
                            {companyOptions.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[11px]">Title</Label>
                        <Input value={selected.title || ""}
                          onChange={(e) => patchField("title", e.target.value)}
                          data-testid="legal-doc-title-input" />
                      </div>
                      <div>
                        <Label className="text-[11px]">Type</Label>
                        <Select value={selected.doc_type || "custom"}
                          onValueChange={(v) => patchField("doc_type", v)}>
                          <SelectTrigger data-testid="legal-doc-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[11px]">Effective date</Label>
                        <Input type="date" value={selected.effective_date || ""}
                          onChange={(e) => patchField("effective_date", e.target.value)}
                          data-testid="legal-doc-effective-input" />
                      </div>
                      <div className="md:col-span-3">
                        <Label className="text-[11px]">Link to client (optional)</Label>
                        <Select value={selected.client_id || "__none__"}
                          onValueChange={(v) => patchField("client_id", v === "__none__" ? null : v)}>
                          <SelectTrigger data-testid="legal-doc-client-select"><SelectValue placeholder="No client link" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— none —</SelectItem>
                            {clients.map((c) => (
                              <SelectItem key={c.client_id} value={c.client_id}>
                                {c.company_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-3">
                        <Label className="text-[11px]">Footer note (rendered at the end of the PDF)</Label>
                        <Input value={selected.footer_note || ""}
                          onChange={(e) => patchField("footer_note", e.target.value)}
                          data-testid="legal-doc-footer-input" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Clients signed to this template */}
                <LegalAgreementsCard doc={selected} clients={clients} />

                {/* Editor + preview */}
                <div className={`grid gap-4 ${preview ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
                  <Card className="rounded-sm">
                    <CardContent className="p-3">
                      <Label className="text-[11px] block mb-1.5">Body</Label>
                      <MarkdownEditor
                        value={selected.body_md || ""}
                        onChange={(v) => patchField("body_md", v)}
                        testid="legal-doc-body-editor"
                        minHeight="70vh"
                      />
                    </CardContent>
                  </Card>
                  {preview && (
                    <Card className="rounded-sm">
                      <CardContent className="p-3">
                        <Label className="text-[11px] block mb-1.5">Live preview</Label>
                        <LivePreview md={selected.body_md || ""} />
                      </CardContent>
                    </Card>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* New-document dialog */}
      <Dialog open={newDialog} onOpenChange={setNewDialog}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle>New legal document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">Company (from Sender agencies)</Label>
              <Select value={nf.company_name}
                onValueChange={(v) => setNf((s) => ({ ...s, company_name: v }))}>
                <SelectTrigger data-testid="legal-doc-new-company">
                  <SelectValue placeholder="Pick a sender agency" />
                </SelectTrigger>
                <SelectContent>
                  {companyOptions.length === 0 && (
                    <div className="px-3 py-2 text-[11px] text-zinc-500">
                      No sender agencies yet — add one under Settings →
                      Sender agencies.
                    </div>
                  )}
                  {companyOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Link to client (optional)</Label>
              <Select value={nf.client_id || "__none__"}
                onValueChange={(v) => setNf((s) => ({ ...s, client_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="No client link" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Title</Label>
              <Input value={nf.title}
                onChange={(e) => setNf((v) => ({ ...v, title: e.target.value }))}
                placeholder="e.g. Service Level Agreement"
                data-testid="legal-doc-new-title" />
            </div>
            <div>
              <Label className="text-[11px]">Document type</Label>
              <Select value={nf.doc_type} onValueChange={(v) => setNf((s) => ({ ...s, doc_type: v }))}>
                <SelectTrigger data-testid="legal-doc-new-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Version numbering style</Label>
              <Select value={nf.version_style} onValueChange={(v) => setNf((s) => ({ ...s, version_style: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dot">v1.0, v1.1, v1.2 …</SelectItem>
                  <SelectItem value="padded">v1.000, v1.001, v1.002 …</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialog(false)}>Cancel</Button>
            <Button onClick={createDoc} data-testid="legal-doc-create-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate dialog */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>Duplicate “{selected?.title}” to another agency</DialogTitle>
            <DialogDescription className="text-xs">
              Creates an independent copy under the chosen agency.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">
              Copies the full document body as a fresh <b>v1.0 draft</b> under the
              agency you pick (e.g. reuse an Iceberg SLA for Image Traders).
              Version history and client agreements do NOT carry over.
            </p>
            <div>
              <Label className="text-[11px]">Target agency</Label>
              <Select value={dupCompany} onValueChange={setDupCompany}>
                <SelectTrigger data-testid="legal-doc-dup-company">
                  <SelectValue placeholder="Pick a sender agency" />
                </SelectTrigger>
                <SelectContent>
                  {companyOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Title for the copy</Label>
              <Input value={dupTitle} onChange={(e) => setDupTitle(e.target.value)}
                data-testid="legal-doc-dup-title" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}>Cancel</Button>
            <Button onClick={duplicate} disabled={dupBusy}
              data-testid="legal-doc-dup-confirm">
              {dupBusy ? "Duplicating…" : "Create copy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader><DialogTitle>Version history</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {versions.length === 0 && <div className="text-sm text-zinc-500">No saved versions.</div>}
            {versions.map((v) => (
              <div key={v.version_id}
                className="rounded-sm border border-zinc-200 p-3 flex items-center gap-3"
                data-testid={`legal-doc-version-${v.version_string}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-sm bg-[#002FA7]/10 text-[#002FA7] border-transparent">{v.version_string}</Badge>
                    <span className="text-[11px] text-zinc-500">
                      {new Date(v.created_at).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {v.created_by ? ` · ${v.created_by}` : ""}
                    </span>
                  </div>
                  {v.change_note && (
                    <div className="text-[12px] text-zinc-600 mt-1">{v.change_note}</div>
                  )}
                </div>
                <Button size="sm" variant="outline"
                  onClick={() => loadVersion(v)}
                  data-testid={`legal-doc-restore-${v.version_string}`}>
                  Load into editor
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
